import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { INTAKE } from "@/lib/preventionData";

const BLUE = "#4C9EDA";
const INK = "#122534";

export default function Intake({ onDone, onLeave }) {
  const [step, setStep] = useState(0);
  const [age, setAge] = useState(38);
  const [sex, setSex] = useState(null);
  const [flags, setFlags] = useState([]);
  const t = INTAKE[step];

  const next = (payload = {}) => {
    if (step + 1 >= INTAKE.length) onDone({ age, sex, flags, ...payload });
    else setStep(step + 1);
  };

  return (
    <div className="w-full max-w-2xl" data-testid="prevention-intake">
      <div className="flex gap-1.5 mb-9 max-w-[180px]">
        {INTAKE.map((_, i) => (
          <span key={i} className="h-[3px] flex-1 rounded-full overflow-hidden" style={{ background: "rgba(18,37,52,0.12)" }}>
            <motion.span className="block h-full" initial={false} animate={{ width: i <= step ? "100%" : "0%" }} transition={{ duration: 0.4 }} style={{ background: BLUE }} />
          </span>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={t.id} initial={{ opacity: 0, y: 24, filter: "blur(6px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} exit={{ opacity: 0, y: -18, filter: "blur(6px)" }} transition={{ duration: 0.48, ease: [0.22, 1, 0.36, 1] }}>
          <h2 className="font-display text-[32px] sm:text-[44px] leading-[1.04]" style={{ color: INK }}>{t.prompt}</h2>
          <p className="text-[15px] mt-3.5 max-w-lg" style={{ color: "rgba(18,37,52,0.58)" }}>{t.sub}</p>

          {t.kind === "number" && (
            <div className="mt-9">
              <div className="flex items-end gap-6 flex-wrap">
                <p className="font-display text-[64px] sm:text-[80px] leading-none tabular-nums" style={{ color: BLUE }}>{age}</p>
                <button onClick={() => next()} className="rounded-full px-7 py-4 text-sm font-semibold text-white inline-flex items-center gap-2.5 mb-3"
                  style={{ background: "#2E6FA3", boxShadow: "0 22px 46px -22px rgba(46,111,163,0.8)" }} data-testid="intake-age-next">
                  Go on <ArrowRight size={15} />
                </button>
              </div>
              <input type="range" min={t.min} max={t.max} value={age} onChange={(e) => setAge(Number(e.target.value))}
                className="w-full mt-7" style={{ accentColor: BLUE }} aria-label="age" data-testid="intake-age" />
              <div className="flex justify-between mt-1 text-[11px]" style={{ color: "rgba(18,37,52,0.35)" }}><span>{t.min}</span><span>{t.max}</span></div>
            </div>
          )}

          {t.kind === "options" && (
            <div className="mt-8 space-y-2.5">
              {t.options.map((o, i) => (
                <motion.button key={o.key} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.08 + i * 0.06 }}
                  onClick={() => { setSex(o.key); next({ sex: o.key }); }}
                  className="group w-full text-left rounded-2xl px-5 py-4 flex items-center gap-4 transition-all hover:-translate-y-0.5"
                  style={{ background: "rgba(255,255,255,0.85)", border: "1px solid rgba(18,37,52,0.08)" }} data-testid={`intake-sex-${o.key}`}>
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: `${BLUE}88` }} />
                  <span className="font-display text-[17px] sm:text-lg" style={{ color: INK }}>{o.label}</span>
                  <ArrowRight size={14} className="ml-auto opacity-0 group-hover:opacity-70 transition-opacity" style={{ color: BLUE }} />
                </motion.button>
              ))}
            </div>
          )}

          {t.kind === "multi" && (
            <div className="mt-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {t.options.map((o, i) => {
                  const on = flags.includes(o.key);
                  return (
                    <motion.button key={o.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 + i * 0.05 }}
                      onClick={() => setFlags((f) => (on ? f.filter((x) => x !== o.key) : [...f, o.key]))}
                      className="text-left rounded-2xl px-4 py-3.5 flex items-start gap-3 transition-all hover:-translate-y-0.5"
                      style={on ? { background: `${BLUE}1C`, border: `1px solid ${BLUE}66` } : { background: "rgba(255,255,255,0.85)", border: "1px solid rgba(18,37,52,0.08)" }}
                      data-testid={`intake-flag-${o.key}`}>
                      <span className="w-[18px] h-[18px] rounded-md flex items-center justify-center shrink-0 mt-0.5"
                        style={on ? { background: BLUE, color: "#fff" } : { border: "1.5px solid rgba(18,37,52,0.22)" }}>{on && <Check size={11} />}</span>
                      <span className="text-[14px] leading-snug" style={{ color: INK }}>{o.label}</span>
                    </motion.button>
                  );
                })}
              </div>
              <button onClick={() => onDone({ age, sex, flags })} className="mt-8 rounded-full px-8 py-4 text-sm font-semibold text-white inline-flex items-center gap-2.5"
                style={{ background: "#2E6FA3", boxShadow: "0 22px 46px -22px rgba(46,111,163,0.8)" }} data-testid="intake-build">
                Build my timeline <ArrowRight size={15} />
              </button>
            </div>
          )}

          <button onClick={() => (step > 0 ? setStep(step - 1) : onLeave())} className="mt-8 text-xs inline-flex items-center gap-1.5" style={{ color: "rgba(18,37,52,0.4)" }} data-testid="intake-back">
            <ArrowLeft size={11} /> {step > 0 ? "back" : "not now"}
          </button>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
